import React, { useState, useEffect } from 'react';
import {
	Card,
	CardContent,
	Grid,
	TextField,
	Autocomplete,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	FormControlLabel,
	Switch,
	Button,
	Typography,
	Collapse,
	IconButton,
	Chip,
	Box,
	Divider,
} from '@mui/material';
import { Search, Clear, ExpandMore, ExpandLess, FilterList } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import makeStyles from '@mui/styles/makeStyles';

import { ICity, IPrechargedServiceFilters } from '../../types';
import { useDebounceState } from 'features/common/hooks/useDebounceState';

const useStyles = makeStyles((theme) => ({
	filterCard: {
		marginBottom: theme.spacing(2),
		borderRadius: theme.spacing(2),
		boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
	},
	filterHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingBottom: theme.spacing(1),
	},
	filterTitle: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		fontWeight: 'bold',
		color: theme.palette.primary.main,
	},
	activeFiltersContainer: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: theme.spacing(0.5),
		marginBottom: theme.spacing(1),
	},
	buttonGroup: {
		display: 'flex',
		gap: theme.spacing(1),
		justifyContent: 'flex-end',
		marginTop: theme.spacing(2),
	},
	secondaryFiltersHeader: {
		display: 'flex',
		alignItems: 'center',
		cursor: 'pointer',
		padding: theme.spacing(1, 0),
		'&:hover': {
			backgroundColor: theme.palette.action.hover,
			borderRadius: theme.spacing(1),
		},
	},
}));

interface Props {
	cities: ICity[];
	filters: IPrechargedServiceFilters;
	onFiltersChange: (filters: IPrechargedServiceFilters) => void;
	onApplyFilters: () => void;
	loading?: boolean;
}

export const PrechargedServicesFilters = ({
	cities,
	filters,
	onFiltersChange,
	onApplyFilters,
	loading = false,
}: Props): JSX.Element => {
	const classes = useStyles();
	const [showSecondaryFilters, setShowSecondaryFilters] = useState(false);

	// Estados locales para campos con debounce
	const [nameInput, setNameInput] = useState(filters.name || '');
	const [observationsInput, setObservationsInput] = useState(filters.observations || '');

	// Valores con debounce
	const debouncedName = useDebounceState(nameInput, 500); // 500ms de delay
	const debouncedObservations = useDebounceState(observationsInput, 500);

	// Actualizar filtros cuando cambien los valores con debounce
	useEffect(() => {
		if (debouncedName !== filters.name) {
			handleFilterChange('name', debouncedName);
			// Auto-aplicar filtros después del debounce
			if (debouncedName !== (filters.name || '')) {
				setTimeout(() => onApplyFilters(), 100);
			}
		}
	}, [debouncedName]);

	useEffect(() => {
		if (debouncedObservations !== filters.observations) {
			handleFilterChange('observations', debouncedObservations);
			// Auto-aplicar filtros después del debounce
			if (debouncedObservations !== (filters.observations || '')) {
				setTimeout(() => onApplyFilters(), 100);
			}
		}
	}, [debouncedObservations]);

	// Sincronizar inputs cuando se limpian los filtros externamente
	useEffect(() => {
		setNameInput(filters.name || '');
		setObservationsInput(filters.observations || '');
	}, [filters.name, filters.observations]);

	const handleFilterChange = (field: keyof IPrechargedServiceFilters, value: any) => {
		onFiltersChange({
			...filters,
			[field]: value,
		});
	};

	const handleClearFilters = () => {
		onFiltersChange({});
		// También limpiar los inputs locales
		setNameInput('');
		setObservationsInput('');
	};

	const hasActiveFilters = Object.values(filters).some(
		(value) => value !== undefined && value !== null && value !== '',
	);

	const getActiveFiltersCount = () => {
		return Object.values(filters).filter((value) => value !== undefined && value !== null && value !== '').length;
	};

	const renderActiveFilters = () => {
		const activeFilters = [];

		if (filters.destinationCodes) {
			const cityName = cities.find((c) => c.code === filters.destinationCodes)?.name || filters.destinationCodes;
			activeFilters.push(
				<Chip
					key="destination"
					label={`Destino: ${cityName}`}
					size="small"
					onDelete={() => handleFilterChange('destinationCodes', '')}
				/>,
			);
		}
		if (filters.name) {
			activeFilters.push(
				<Chip
					key="name"
					label={`Nombre: ${filters.name}`}
					size="small"
					onDelete={() => {
						handleFilterChange('name', '');
						setNameInput('');
					}}
				/>,
			);
		}
		if (filters.serviceTypeName) {
			activeFilters.push(
				<Chip
					key="serviceType"
					label={`Tipo: ${filters.serviceTypeName}`}
					size="small"
					onDelete={() => handleFilterChange('serviceTypeName', '')}
				/>,
			);
		}
		if (filters.enable !== undefined) {
			activeFilters.push(
				<Chip
					key="enable"
					label={`Estado: ${filters.enable ? 'Activo' : 'Inactivo'}`}
					size="small"
					onDelete={() => handleFilterChange('enable', undefined)}
				/>,
			);
		}
		if (filters.observations) {
			activeFilters.push(
				<Chip
					key="observations"
					label={`Observaciones: ${filters.observations}`}
					size="small"
					onDelete={() => {
						handleFilterChange('observations', '');
						setObservationsInput('');
					}}
				/>,
			);
		}
		if (filters.lastUpdate) {
			activeFilters.push(
				<Chip
					key="lastUpdate"
					label={`Fecha: ${filters.lastUpdate}`}
					size="small"
					onDelete={() => handleFilterChange('lastUpdate', '')}
				/>,
			);
		}
		if (filters.forAllDestinations !== undefined) {
			activeFilters.push(
				<Chip
					key="forAllDestinations"
					label={`Todos destinos: ${filters.forAllDestinations ? 'Sí' : 'No'}`}
					size="small"
					onDelete={() => handleFilterChange('forAllDestinations', undefined)}
				/>,
			);
		}
		if (filters.availableInQuotation !== undefined) {
			activeFilters.push(
				<Chip
					key="availableInQuotation"
					label={`Disponible en cotizador: ${filters.availableInQuotation ? 'Sí' : 'No'}`}
					size="small"
					onDelete={() => handleFilterChange('availableInQuotation', undefined)}
				/>,
			);
		}

		return activeFilters;
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
			<Card className={classes.filterCard}>
				<CardContent>
					<div className={classes.filterHeader}>
						<Typography variant="h6" className={classes.filterTitle}>
							<FilterList />
							Filtros
							{hasActiveFilters && (
								<Chip label={`${getActiveFiltersCount()} activos`} size="small" color="primary" variant="outlined" />
							)}
						</Typography>

						<Box className={classes.buttonGroup}>
							<Button
								variant="outlined"
								size="small"
								onClick={handleClearFilters}
								disabled={!hasActiveFilters || loading}
								startIcon={<Clear />}
							>
								Limpiar
							</Button>
							<Button
								variant="contained"
								size="small"
								onClick={onApplyFilters}
								disabled={loading}
								startIcon={<Search />}
							>
								Aplicar
							</Button>
						</Box>
					</div>

					{hasActiveFilters && <div className={classes.activeFiltersContainer}>{renderActiveFilters()}</div>}

					{/* Filtros principales */}
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6} md={2.4}>
							<Autocomplete
								size="small"
								options={cities}
								getOptionLabel={(option) => `${option.name} (${option.code})`}
								value={cities.find((c) => c.code === filters.destinationCodes) || null}
								onChange={(_, newValue) => handleFilterChange('destinationCodes', newValue?.code || '')}
								renderInput={(params) => (
									<TextField {...params} label="Destino" placeholder="Seleccionar ciudad..." fullWidth />
								)}
								disabled={loading}
							/>
						</Grid>

						<Grid item xs={12} sm={6} md={2.4}>
							<TextField
								size="small"
								fullWidth
								label="Nombre del servicio"
								placeholder="Buscar por nombre..."
								value={nameInput}
								onChange={(e) => setNameInput(e.target.value)}
								disabled={loading}
								helperText={nameInput !== debouncedName ? 'Escribiendo...' : ''}
							/>
						</Grid>

						<Grid item xs={12} sm={6} md={2.4}>
							<TextField
								size="small"
								fullWidth
								label="Tipo de servicio"
								placeholder="Ej: tour, hotel..."
								value={filters.serviceTypeName || ''}
								onChange={(e) => handleFilterChange('serviceTypeName', e.target.value)}
								disabled={loading}
							/>
						</Grid>

						<Grid item xs={12} sm={6} md={2.4}>
							<FormControl size="small" fullWidth disabled={loading}>
								<InputLabel>Estado</InputLabel>
								<Select
									value={filters.enable === undefined ? '' : filters.enable.toString()}
									onChange={(e) => {
										const value = e.target.value;
										handleFilterChange('enable', value === '' ? undefined : value === 'true');
									}}
									label="Estado"
								>
									<MenuItem value="">Todos</MenuItem>
									<MenuItem value="true">Activo</MenuItem>
									<MenuItem value="false">Inactivo</MenuItem>
								</Select>
							</FormControl>
						</Grid>

						<Grid item xs={12} sm={6} md={2.4}>
							<FormControl size="small" fullWidth disabled={loading}>
								<InputLabel>Disponible en cotizador</InputLabel>
								<Select
									value={filters.availableInQuotation === undefined ? '' : filters.availableInQuotation.toString()}
									onChange={(e) => {
										const value = e.target.value;
										handleFilterChange('availableInQuotation', value === '' ? undefined : value === 'true');
									}}
									label="Disponible en cotizador"
								>
									<MenuItem value="">Todos</MenuItem>
									<MenuItem value="true">Sí</MenuItem>
									<MenuItem value="false">No</MenuItem>
								</Select>
							</FormControl>
						</Grid>
					</Grid>

					{/* Filtros secundarios */}
					<Divider sx={{ my: 2 }} />

					<div
						className={classes.secondaryFiltersHeader}
						onClick={() => setShowSecondaryFilters(!showSecondaryFilters)}
					>
						<Typography variant="body2" color="textSecondary">
							Filtros adicionales
						</Typography>
						<IconButton size="small">{showSecondaryFilters ? <ExpandLess /> : <ExpandMore />}</IconButton>
					</div>

					<Collapse in={showSecondaryFilters} timeout="auto" unmountOnExit>
						<Grid container spacing={2} sx={{ mt: 1 }}>
							<Grid item xs={12} sm={6} md={4}>
								<TextField
									size="small"
									fullWidth
									label="Observaciones"
									placeholder="Buscar en observaciones..."
									value={observationsInput}
									onChange={(e) => setObservationsInput(e.target.value)}
									disabled={loading}
									helperText={observationsInput !== debouncedObservations ? 'Escribiendo...' : ''}
								/>
							</Grid>

							<Grid item xs={12} sm={6} md={4}>
								<DatePicker
									label="Última actualización"
									value={filters.lastUpdate ? new Date(filters.lastUpdate) : null}
									onChange={(newValue) => {
										const dateString = newValue ? newValue.toISOString().split('T')[0] : '';
										handleFilterChange('lastUpdate', dateString);
									}}
									renderInput={(params) => <TextField {...params} size="small" fullWidth disabled={loading} />}
									disabled={loading}
								/>
							</Grid>

							<Grid item xs={12} sm={6} md={4}>
								<FormControlLabel
									control={
										<Switch
											checked={filters.forAllDestinations || false}
											onChange={(e) => handleFilterChange('forAllDestinations', e.target.checked ? true : undefined)}
											disabled={loading}
										/>
									}
									label="Para todos los destinos"
								/>
							</Grid>
						</Grid>
					</Collapse>
				</CardContent>
			</Card>
		</LocalizationProvider>
	);
};
